import {clsx} from 'clsx'
import styles from './MergeBoxExpandable.module.css'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {useState} from 'react'
import type React from 'react'

type MergeBoxExpandableProps = {
  isExpanded?: boolean
  wrapperClassName?: string
  contentClassName?: string
  contentPaddingInline?: 'none' | 'condensed'
  children: React.ReactNode
}

/**
 * Handles shared style treatment around collapsing Expandables
 */
export const MergeBoxExpandable = ({
  isExpanded = false,
  wrapperClassName,
  contentClassName,
  contentPaddingInline = 'condensed',
  children,
}: MergeBoxExpandableProps) => {
  // Keep children mounted while the collapse animation runs so the drawer can
  // animate closed. Unmounting them the instant `isExpanded` flips false would
  // leave nothing for the `grid-template-rows` transition to collapse, causing
  // the drawer to snap shut. They're unmounted once that transition finishes.
  const [renderChildren, setRenderChildren] = useState(isExpanded)

  // Under reduced motion the transition duration is `0`, which never emits a
  // `transitionend`, so the event-based unmount below would never fire. Unmount
  // immediately in that case (there's no animation to preserve).
  const prefersReducedMotion = usePrefersReducedMotion()

  if (isExpanded && !renderChildren) {
    setRenderChildren(true)
  } else if (!isExpanded && renderChildren && prefersReducedMotion) {
    setRenderChildren(false)
  }

  return (
    <div
      className={clsx(styles.expandableWrapper, isExpanded && styles.isExpanded, wrapperClassName)}
      //  We're setting visibility directly because tests don't pick up styles from modules
      style={{visibility: isExpanded ? 'visible' : 'hidden'}}
      // `visibility` is transitioned, so it stays computed-visible for the whole close
      // animation — leaving the retained subtree's controls/links in the tab order and
      // a11y tree even though the disclosure already reports `aria-expanded=false`. Mark
      // the retained content `inert` while collapsed so it can animate out without
      // remaining interactive (mirrors `AnimatedCollapse`).
      inert={!isExpanded || undefined}
      onTransitionEnd={event => {
        // `transitionend` bubbles, and nested expanders also transition `grid-template-rows`.
        // Scope to this wrapper's own transition so a descendant's event can't unmount our
        // content before its own close animation finishes.
        if (event.target === event.currentTarget && event.propertyName === 'grid-template-rows' && !isExpanded) {
          setRenderChildren(false)
        }
      }}
    >
      <div
        className={clsx(
          styles.expandableContent,
          isExpanded && styles.isExpanded,
          contentPaddingInline === 'none' && styles.paddingInlineNone,
          contentClassName,
        )}
      >
        {renderChildren && children}
      </div>
    </div>
  )
}
